import type { FC, ReactNode } from 'react'
import type { Hill } from '@/lib/contours'
import type { NavLink } from '@/types'

import Image from 'next/image'
import { ArrowUpRight } from 'lucide-react'
import { useRef, useState } from 'react'

import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'
import { ContourField } from '@/components/generals/contour-field'
import { MoreLink } from '@/components/generals/more-link'
import { SectionLabel } from '@/components/generals/section-label'
import { SplitReveal } from '@/components/generals/split-reveal'
import { StatValue } from '@/components/generals/stat-value'
import { useEnquiry } from '@/components/landing/enquiry-context'

import {
    DESKTOP,
    gsap,
    ScrollTrigger,
    useGSAP
} from '@/lib/gsap'

import { academyStats, facilities } from '@/constants/experience'

const ACADEMY_HILLS: Hill[] = [
    { x: 1300, y: 260, radius: 320, rings: 11, seed: 3.1 },
    { x: 200, y: 900, radius: 260, rings: 9, seed: 1.4 }
]

interface AcademySectionProps {
    index?: string
    more?: NavLink
}

export const AcademySection: FC<AcademySectionProps> = ({ index = '05', more }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const [active, setActive] = useState(0)
    const { requestEnquiry } = useEnquiry()

    useGSAP(() => {
        const mm = gsap.matchMedia()

        // On larger screens the preview follows whichever facility sits mid-screen.
        mm.add(DESKTOP, () => {
            gsap.utils.toArray<HTMLElement>('[data-facility]').forEach((item, index) => {
                ScrollTrigger.create({
                    trigger: item,
                    start: 'top 62%',
                    end: 'bottom 62%',
                    onToggle: (self) => {
                        if (self.isActive) setActive(index)
                    }
                })
            })
        })

        return () => mm.revert()
    }, { scope: sectionRef })

    const current = facilities[active]

    return (
        <section
            ref={sectionRef}
            id='academy'
            aria-labelledby='academy-heading'
            className='relative z-10 bg-forest text-bone'
        >
            <ContourField
                hills={ACADEMY_HILLS}
                viewBox='0 0 1600 1200'
                className='pointer-events-none absolute inset-0 h-full w-full text-bone opacity-[0.06]'
            />

            <div className='relative px-5 py-24 sm:px-8 lg:px-12 lg:py-36'>
                <div className='grid gap-10 lg:grid-cols-12 lg:gap-8'>
                    <div className='lg:col-span-7'>
                        <SectionLabel index={index} className='text-bone/70'>
                            Academy & practice
                        </SectionLabel>

                        <SplitReveal
                            as='h2'
                            id='academy-heading'
                            className='mt-6 text-[clamp(2.3rem,4.6vw,4.6rem)] font-light leading-[1.02] tracking-[-0.03em]'
                        >
                            Practice, <em className='font-serif font-normal'>measured to the millimetre.</em>
                        </SplitReveal>
                    </div>

                    <div className='self-end lg:col-span-4 lg:col-start-9'>
                        <p data-reveal className='text-base leading-relaxed text-bone/75'>
                            Every bay, green and bunker in the Academy is tracked, so practice turns into progress you can see. The technology stays out of sight. The feedback doesn&apos;t.
                        </p>

                        <div data-reveal className='mt-8 flex flex-wrap items-center gap-x-8 gap-y-5'>
                            <button
                                type='button'
                                onClick={() => requestEnquiry({ type: 'lesson' })}
                                className='inline-flex h-12 items-center gap-3 rounded-full bg-bone px-6 text-[12px] font-semibold uppercase tracking-[0.16em] text-charcoal transition-colors hover:bg-ochre'
                            >
                                Book a lesson
                                <ArrowUpRight className='size-4' aria-hidden='true' />
                            </button>

                            {more && <MoreLink href={more.href} label={more.label} />}
                        </div>
                    </div>
                </div>

                <div className='mt-16 grid gap-10 lg:mt-24 lg:grid-cols-12 lg:gap-8'>
                    <ol className='lg:col-span-5'>
                        {facilities.map((facility, index) => (
                            <li
                                key={facility.name}
                                data-facility
                                onMouseEnter={() => setActive(index)}
                                className={cn(
                                    'border-t border-bone/15 py-8 transition-opacity duration-500 last:border-b lg:py-12',
                                    index === active ? 'lg:opacity-100' : 'lg:opacity-45 lg:hover:opacity-80'
                                )}
                            >
                                <div className='relative mb-6 aspect-[16/10] overflow-hidden rounded-[4px] lg:hidden'>
                                    <Image
                                        src={facility.image.src}
                                        alt={facility.image.alt}
                                        fill
                                        sizes='100vw'
                                        className='object-cover'
                                        style={{ objectPosition: facility.image.position }}
                                    />
                                </div>

                                <div className='flex items-baseline gap-5'>
                                    <span className='font-mono text-[11px] tabular-nums text-bone/50'>
                                        {pad(index + 1)}
                                    </span>

                                    <h3 className='type-wide text-[clamp(1.5rem,2.4vw,2.2rem)] font-medium uppercase leading-none'>
                                        {facility.name}
                                    </h3>
                                </div>

                                <p className='mt-4 max-w-md pl-10 text-[15px] leading-relaxed text-bone/75'>
                                    {facility.description}
                                </p>

                                <ul className='mt-5 flex flex-wrap gap-2 pl-10'>
                                    {facility.specs.map((spec) => (
                                        <li key={spec} className='rounded-full border border-bone/20 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-bone/80'>
                                            {spec}
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        ))}
                    </ol>

                    <div className='hidden lg:col-span-7 lg:block'>
                        <div className='sticky top-24'>
                            <div className='relative aspect-[4/5] max-h-[calc(100svh-8rem)] w-full overflow-hidden rounded-[4px] bg-pine xl:aspect-[5/6]'>
                                {facilities.map((facility, index) => (
                                    <div
                                        key={facility.name}
                                        aria-hidden={index !== active}
                                        className={cn(
                                            'absolute inset-0 transition-[opacity,transform] duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)]',
                                            index === active ? 'scale-100 opacity-100' : 'scale-[1.08] opacity-0'
                                        )}
                                    >
                                        <Image
                                            src={facility.image.src}
                                            alt={facility.image.alt}
                                            fill
                                            sizes='55vw'
                                            className='object-cover'
                                            style={{ objectPosition: facility.image.position }}
                                        />
                                    </div>
                                ))}

                                <div aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-pine/80 via-transparent to-pine/20' />

                                <div className='absolute inset-x-0 top-0 flex justify-between p-6 font-mono text-[11px] uppercase tracking-[0.22em] text-bone/85'>
                                    <span>
                                        The Academy
                                    </span>

                                    <span>
                                        {pad(active + 1)} / {pad(facilities.length)}
                                    </span>
                                </div>

                                <div key={current.name} className='absolute inset-x-0 bottom-0 p-6 animate-in fade-in slide-in-from-bottom-4 duration-700'>
                                    <p className='type-wide text-3xl font-medium uppercase'>
                                        {current.name}
                                    </p>

                                    <p className='mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-bone/75'>
                                        {current.specs.join(' · ')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <dl className='mt-20 grid grid-cols-2 gap-x-6 gap-y-10 border-t border-bone/15 pt-10 lg:mt-28 lg:grid-cols-4'>
                    {academyStats.map((stat) => (
                        <div key={stat.label} data-reveal className='flex flex-col-reverse'>
                            <dt className='mt-2 max-w-[14rem] text-sm leading-snug text-bone/65'>
                                {stat.label}
                            </dt>

                            <dd className='type-wide text-[clamp(2rem,3.6vw,3.4rem)] font-light leading-none tracking-[-0.03em]'>
                                <StatValue stat={stat} />
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>
        </section>
    )
}
